import React, { useEffect, useState } from "react";
import { Copy, Gift, Check, Users, Sparkles, Loader2 } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";

/**
 * ReferralsCard — self-contained tile that:
 *  1. Fetches /api/referrals/me on mount
 *  2. Shows the shareable link (copy-to-clipboard)
 *  3. Shows stats: friends joined, free months earned, cap remaining
 *
 * Renders nothing if the user isn't paying (402 from backend).
 * `variant`:
 *   "dashboard" — full card (used inside /csuite/dashboard)
 *   "success"   — compact card (used on /payment/success)
 */
export default function ReferralsCard({ variant = "dashboard" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    csuiteApi.referralsMe()
      .then(setData)
      .catch(e => setErr(e.message || "Failed to load referrals"))
      .finally(() => setBusy(false));
  }, []);

  const copy = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  // Silent 402 → hide the tile entirely
  const isPaywalled = err.toLowerCase().includes("paying subscriber");
  if (isPaywalled) return null;

  if (busy) {
    return (
      <div className="card p-5 flex items-center gap-2 text-sm text-zinc-500" data-testid="referrals-loading">
        <Loader2 size={14} className="animate-spin"/> Loading referrals…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="card p-5 text-sm text-amber-700" data-testid="referrals-error">
        Couldn&rsquo;t load referrals: {err}
      </div>
    );
  }

  const compact = variant === "success";

  return (
    <div className="card p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#FFF7ED 0%,#FEF3C7 100%)", borderColor: "#F59E0B33" }} data-testid="referrals-card">
      {/* Ambient icon */}
      <div className="absolute -right-4 -top-4 opacity-10">
        <Gift size={100} strokeWidth={1.2} />
      </div>

      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-sm bg-amber-100 grid place-items-center text-amber-700"><Gift size={16}/></div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800">Referral rewards</div>
          <div className="font-display text-lg font-extrabold tracking-tight text-zinc-900">Invite a friend, get a free month</div>
        </div>
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed mt-2 max-w-2xl">
        When a friend subscribes with your link, <b>both of you</b> get an account credit worth one full month of your plan (Starter $200 or Platinum $350) automatically applied to your next invoice.
      </p>

      {/* Share link */}
      <div className="mt-4 flex items-center gap-2">
        <div className="flex-1 min-w-0 bg-white border border-[var(--border)] rounded-sm px-3 py-2 font-mono text-xs text-zinc-800 truncate" data-testid="referrals-share-url">
          {data.share_url}
        </div>
        <button onClick={copy} className={`btn btn-sm inline-flex items-center gap-1.5 ${copied ? "btn-secondary" : "btn-primary"}`} data-testid="referrals-copy-btn">
          {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
        </button>
      </div>

      {/* Stats */}
      {!compact && (
        <div className="mt-5 grid grid-cols-3 gap-3">
          <Stat icon={Users}    label="Friends joined"  value={data.referred_count}  testid="referrals-stat-joined"/>
          <Stat icon={Sparkles} label="Rewards earned"  value={data.credits_earned}  testid="referrals-stat-earned"/>
          <Stat icon={Gift}     label={`Cap · ${data.cap_yearly}/yr`} value={data.cap_remaining} sub="remaining" testid="referrals-stat-cap"/>
        </div>
      )}

      {/* Recent referrals table (dashboard only) */}
      {!compact && data.referred?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-amber-200/60">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-800 mb-2">Recent invitees</div>
          <div className="space-y-1.5">
            {data.referred.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs" data-testid={`referrals-row-${i}`}>
                <span className="font-mono text-zinc-700">{r.email_masked}</span>
                <span className={`pill text-[9px] ${r.rewarded ? "pill-green" : r.status === "active" ? "pill-blue" : "pill-neutral"}`}>
                  {r.rewarded ? "Rewarded" : r.status === "active" ? "Subscribed" : "Pending"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Small print */}
      <div className="mt-4 text-[10px] font-mono text-amber-800/70">
        Reward triggers on referred user&rsquo;s first successful payment · self-referral blocked · max {data.cap_yearly}/year
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-sm border border-amber-200/60 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-amber-800">
        <Icon size={11}/> {label}
      </div>
      <div className="font-display text-2xl font-extrabold text-zinc-900 mt-0.5">{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-500">{sub}</div>}
    </div>
  );
}
